import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Save, Upload, ImageIcon, Loader2 } from "lucide-react";

const FIELDS = [
  ["company_name", "Název firmy"], ["address", "Adresa"], ["phone", "Telefon"],
  ["email", "E-mail"], ["contact_person", "Kontaktní osoba"], ["ico", "IČO"],
  ["dic", "DIČ"], ["web", "Web"],
];

export default function OfferSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const loadLogo = useCallback(async () => {
    try {
      const r = await api.get("/offer-settings/logo", { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(r.data));
    } catch { setLogoUrl(null); }
  }, []);

  useEffect(() => {
    api.get("/offer-settings").then((r) => { setS(r.data); if (r.data.logo_path) loadLogo(); });
  }, [loadLogo]);

  if (!s) return null;
  const up = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/offer-settings", s); toast.success("Nastavení uloženo"); }
    catch (e) { toast.error(e.response?.data?.detail || "Chyba"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/offer-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo nahráno");
      await loadLogo();
    } catch (err) { toast.error(err.response?.data?.detail || "Nahrání se nezdařilo"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Nastavení nabídky" subtitle="Firemní údaje a vzhled nabídkového PDF pro odběratele">
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="offerset-save">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Uložit
        </Button>
      </PageHeader>

      <div className="px-10 py-8 max-w-3xl space-y-6">
        <div className="bg-white border border-border rounded-xl p-8">
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Logo firmy</Label>
          <div className="flex items-center gap-6 mt-4">
            <div className="h-24 w-40 border border-border rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
              {logoUrl ? <img src={logoUrl} alt="logo" className="max-h-full max-w-full object-contain" data-testid="offerset-logo-preview" />
                : <ImageIcon className="h-8 w-8 text-muted-foreground/40" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadLogo} data-testid="offerset-logo-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="offerset-logo-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Nahrát logo
              </Button>
              <p className="text-xs text-muted-foreground mt-2">PNG, JPG nebo WEBP.</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-8 grid grid-cols-2 gap-5">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "address" ? "col-span-2" : ""}>
              <Label>{label}</Label>
              <Input data-testid={`offerset-${k}`} className="mt-1.5" value={s[k] || ""} onChange={(e) => up(k, e.target.value)} />
            </div>
          ))}
        </div>

        <div className="bg-white border border-border rounded-xl p-8 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label>Zobrazovat ceny s DPH</Label>
              <p className="text-xs text-muted-foreground mt-1">V PDF přidá vedle ceny bez DPH i cenu s DPH.</p>
            </div>
            <Switch checked={s.show_vat} onCheckedChange={(v) => up("show_vat", v)} data-testid="offerset-showvat" />
          </div>
          {s.show_vat && (
            <div className="max-w-[160px]">
              <Label>Sazba DPH (%)</Label>
              <Input type="number" data-testid="offerset-vatrate" className="mt-1.5" value={s.vat_rate}
                onChange={(e) => up("vat_rate", parseFloat(e.target.value) || 0)} />
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <Label>Zobrazovat sloupec „Koeficient"</Label>
              <p className="text-xs text-muted-foreground mt-1">V PDF nabídky zobrazí/skryje sloupec s koeficientem u pásem.</p>
            </div>
            <Switch checked={s.show_coefficient !== false} onCheckedChange={(v) => up("show_coefficient", v)} data-testid="offerset-showcoef" />
          </div>
          <div>
            <Label>Text v patičce nabídky</Label>
            <Textarea data-testid="offerset-footer" className="mt-1.5" rows={2} value={s.footer_note || ""}
              onChange={(e) => up("footer_note", e.target.value)} placeholder="např. Ceny jsou platné 30 dní. Doprava v ceně…" />
          </div>
        </div>
      </div>
    </div>
  );
}
